/** Inline-rules bridge for `rule` / `tag` notification blocks.
 *
 * A `rule` block's `definition` is an inline-rules JSON object; a `tag` block
 * may carry the same object under `condition` (kind `rules`). Both hold the
 * ruleset editor's rule rows minus their `id` — the rows are embedded in the
 * block, not owned by a ruleset:
 *
 *     {"match_type": 0, "rules": [{type, tpl, args, field, negate, op, value,
 *                                  nested_ruleset}]}
 *
 * These converters let the shared `RulesEditor` edit that blob: rows get a
 * client-side draft id on the way in (RuleDraft.id doubles as the React key and
 * the expanded-row key, so it must be unique and stable) and lose it on the way
 * out. `nested_ruleset` is normalized to a bare int on write — the read shape
 * `draftsFromDetail` expects — so a saved blob re-opens byte-identically even
 * though the backend also accepts the `{id}` form.
 */
import { allocClientRuleId, draftsFromDetail, rulesToWrite } from './rules/ruleSerialize';
import type { RuleDraft } from './rules/ruleTypes';
import type { RuleWire } from '@/api/notificationRulesets';
import { MATCH_ALL } from '@/api/notificationRulesets';

/** One embedded rule row: the wire row without the owner-assigned `id`. */
export type InlineRuleRow = Omit<RuleWire, 'id'>;

export interface InlineRules {
  match_type: number;
  rules: InlineRuleRow[];
}

export const EMPTY_INLINE_RULES: InlineRules = { match_type: MATCH_ALL, rules: [] };

/** `{match_type, rules}` → editor drafts. Rows are given fresh client ids
 *  (all negative), so every row round-trips back out as an embedded row. */
export function draftsFromInline(inline: InlineRules | null | undefined): {
  matchType: number;
  drafts: RuleDraft[];
} {
  const rows = inline?.rules ?? [];
  const wire: RuleWire[] = rows.map((row) => ({ ...row, id: allocClientRuleId() }));
  return {
    matchType: inline?.match_type ?? MATCH_ALL,
    drafts: draftsFromDetail(wire),
  };
}

/** Editor drafts → `{match_type, rules}`. Drops the per-row `id` and flattens
 *  `nested_ruleset` to a bare id (write rows carry `{id}`; the stored read
 *  shape is the int, which is what `draftsFromInline` consumes). */
export function inlineFromDrafts(matchType: number, drafts: RuleDraft[]): InlineRules {
  const rules: InlineRuleRow[] = rulesToWrite(drafts).map((row) => ({
    type: row.type,
    tpl: row.tpl,
    args: row.args,
    field: row.field,
    negate: row.negate,
    op: row.op,
    value: row.value,
    nested_ruleset: row.nested_ruleset ? row.nested_ruleset.id : null,
  }));
  return { match_type: matchType, rules };
}

/** Parse a `rule` block's `definition`. Returns null when it is empty or not
 *  valid inline-rules JSON — the editor then starts from an empty rule list
 *  rather than throwing on a hand-edited definition. */
export function parseRuleDefinition(definition: string): InlineRules | null {
  const raw = (definition ?? '').trim();
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const obj = parsed as Partial<InlineRules>;
    if (!Array.isArray(obj.rules)) return null;
    return {
      match_type: typeof obj.match_type === 'number' ? obj.match_type : MATCH_ALL,
      rules: obj.rules as InlineRuleRow[],
    };
  } catch {
    return null;
  }
}

/** A `tag` block's `definition`: an optional condition, nothing else. */
export type TagCondition =
  | ({ kind: 'rules' } & InlineRules)
  | { kind: 'ruleset'; spec: string }
  | null;

/** Parse a `tag` block's `definition` into its condition (null = always inject). */
export function parseTagDefinition(definition: string): TagCondition {
  const raw = (definition ?? '').trim();
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const condition = (parsed as { condition?: unknown }).condition;
    if (!condition || typeof condition !== 'object') return null;
    const c = condition as Record<string, unknown>;
    if (c.kind === 'ruleset') {
      return { kind: 'ruleset', spec: typeof c.spec === 'string' ? c.spec : '' };
    }
    if (c.kind === 'rules') {
      return {
        kind: 'rules',
        match_type: typeof c.match_type === 'number' ? c.match_type : MATCH_ALL,
        rules: Array.isArray(c.rules) ? (c.rules as InlineRuleRow[]) : [],
      };
    }
    return null;
  } catch {
    return null;
  }
}

/** Serialize a `tag` block's condition back into its `definition` string.
 *  A null condition (or an empty rule list / blank spec) means "always
 *  inject", which the backend spells as an empty definition. */
export function tagDefinitionFrom(condition: TagCondition): string {
  if (condition === null) return '';
  if (condition.kind === 'ruleset') {
    const spec = condition.spec.trim();
    return spec ? JSON.stringify({ condition: { kind: 'ruleset', spec } }) : '';
  }
  if (!condition.rules.length) return '';
  return JSON.stringify({
    condition: { kind: 'rules', match_type: condition.match_type, rules: condition.rules },
  });
}

/** Serialize a `rule` block's definition. An empty rule list serializes to ''
 *  so the server's "a rule block needs at least one rule" 400 is what the user
 *  sees, rather than a silently-true empty rule set. */
export function ruleDefinitionFrom(inline: InlineRules): string {
  if (!inline.rules.length) return '';
  return JSON.stringify({ match_type: inline.match_type, rules: inline.rules });
}
